/* iter101 — Multi-event SMS templates (late / convocation / bulletin). */
import { useEffect, useState } from 'react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Switch } from '../ui/switch';
import { Label } from '../ui/label';
import { Clock, EnvelopeSimple, FileText, FloppyDisk } from '@phosphor-icons/react';
import { toast } from 'sonner';
import api from '../../lib/api';

const ICONS = { late: Clock, convocation: EnvelopeSimple, bulletin: FileText };
const COLORS = { late: 'text-amber-600', convocation: 'text-indigo-600', bulletin: 'text-emerald-600' };
const SAMPLE = {
  first_name: 'Amina', last_name: 'Diallo', class_name: '6è A',
  date: '12/03/2026', time: '08:15', rdv: '15/03/2026 à 10h00',
  motif: 'Absences répétées', period: 'Trimestre 1',
};

const preview = (tpl, schoolName, signature) =>
  (tpl || '').replace(/\{(\w+)\}/g, (m, k) => {
    if (k === 'school_name') return schoolName;
    if (k === 'signature') return signature;
    return SAMPLE[k] ?? m;
  });

const TemplateCard = ({ ev, schoolName, signature, onSaved }) => {
  const [tpl, setTpl] = useState(ev.template);
  const [enabled, setEnabled] = useState(ev.enabled);
  const [saving, setSaving] = useState(false);
  const Icon = ICONS[ev.key] || FileText;

  const save = async () => {
    if (!tpl.trim()) { toast.error('Le modèle ne peut être vide'); return; }
    setSaving(true);
    try {
      await api.put(`/sms/templates/${ev.key}`, { template: tpl, enabled });
      toast.success(`Modèle « ${ev.label} » enregistré`);
      onSaved?.();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Erreur d'enregistrement");
    } finally { setSaving(false); }
  };

  return (
    <Card className="border border-[#E2E8F0] shadow-none" data-testid={`sms-tpl-card-${ev.key}`}>
      <CardHeader className="pb-2">
        <CardTitle className="font-outfit text-base flex items-center justify-between gap-2">
          <span className="flex items-center gap-2">
            <Icon size={18} weight="duotone" className={COLORS[ev.key]} />
            SMS {ev.label}
          </span>
          <label className="flex items-center gap-2 text-xs font-normal text-[#64748B]">
            <Switch checked={enabled} onCheckedChange={setEnabled}
              data-testid={`sms-tpl-toggle-${ev.key}`} />
            {enabled ? 'Envoi auto activé' : 'Désactivé'}
          </label>
        </CardTitle>
      </CardHeader>
      <CardContent className="space-y-3">
        <textarea value={tpl} rows={4}
          onChange={e => setTpl(e.target.value.slice(0, 320))}
          data-testid={`sms-tpl-textarea-${ev.key}`}
          className="w-full rounded-md border border-[#E2E8F0] p-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#0052FF]/40" />
        <div className="flex items-center justify-between text-[11px] text-[#94A3B8] gap-2">
          <div className="flex flex-wrap gap-1">
            {ev.variables.map(v => (
              <button key={v} type="button" onClick={() => setTpl(p => p + v)}
                data-testid={`sms-tpl-var-${ev.key}-${v.slice(1, -1)}`}
                className="bg-slate-100 hover:bg-slate-200 rounded px-1.5 py-0.5 font-mono text-slate-700">
                {v}
              </button>
            ))}
          </div>
          <span>{tpl.length}/320</span>
        </div>
        <div>
          <Label className="text-xs">Aperçu</Label>
          <div className="mt-1 p-2.5 rounded-md bg-slate-50 border border-slate-200 text-sm whitespace-pre-wrap"
            data-testid={`sms-tpl-preview-${ev.key}`}>
            {preview(tpl, schoolName, signature)}
          </div>
        </div>
        <div className="flex gap-2">
          <Button size="sm" onClick={save} disabled={saving}
            data-testid={`sms-tpl-save-${ev.key}`}
            className="bg-[#0052FF] hover:bg-[#0041CC] text-white">
            <FloppyDisk size={14} className="mr-1.5" />
            {saving ? 'Enregistrement…' : 'Enregistrer'}
          </Button>
          <Button size="sm" variant="outline" onClick={() => setTpl(ev.default)}
            data-testid={`sms-tpl-reset-${ev.key}`}>
            Modèle par défaut
          </Button>
        </div>
      </CardContent>
    </Card>
  );
};

const SmsTemplatesTab = () => {
  const [data, setData] = useState(null);

  const load = async () => {
    try {
      const { data: d } = await api.get('/sms/templates');
      setData(d);
    } catch (e) {
      toast.error(e?.response?.data?.detail || 'Erreur chargement des modèles');
    }
  };
  useEffect(() => { load(); }, []);

  if (!data) return <div className="text-sm text-[#94A3B8]">Chargement…</div>;

  return (
    <div className="space-y-3" data-testid="sms-templates-tab">
      <div>
        <h3 className="font-outfit font-semibold text-[#0F172A]">Modèles SMS par événement</h3>
        <p className="text-xs text-[#64748B]">
          Envoyés automatiquement au contact parent : retard pointé, convocation créée, bulletin publié.
          La signature est celle définie dans « SMS d&apos;anniversaire ».
        </p>
      </div>
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-3">
        {data.events.map(ev => (
          <TemplateCard key={ev.key} ev={ev} schoolName={data.school_name || 'Mon École'}
            signature={data.signature} onSaved={load} />
        ))}
      </div>
    </div>
  );
};

export default SmsTemplatesTab;
